import { useEffect, useRef } from "react";
import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type { RuleGraphDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { useImmer } from "use-immer";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useAgenticLogicRuleVersionDetail } from "@src/api/agentic-logic/get-agentic-logic-rule-version-detail";
import { useValidateAgenticLogicTemplate } from "@src/api/agentic-logic/validate-agentic-logic-template";
import {
  hasUnpublishedRuleConfig,
  hasUnpublishedRuleSettings,
} from "../workspace/unpublishedSettings";

/** Reuse the published graph only when it describes the saved configuration.
 * Each changed configuration gets its own derivation, including later agent edits.
 */
export const useTemplateSelectionGraph = (rule: AgenticLogicRuleDto) => {
  const [derived, setDerived] = useImmer<{
    key: string;
    graph: RuleGraphDto | null;
  } | null>(null);
  const [attemptedKey, setAttemptedKey] = useImmer<string | null>(null);
  const deriveKey = JSON.stringify([
    rule.templateId,
    rule.templateParams,
    rule.settings,
  ]);
  const currentDeriveKey = useRef(deriveKey);
  currentDeriveKey.current = deriveKey;
  const validateMutation = useValidateAgenticLogicTemplate();

  const versionsQuery = useAgenticLogicRuleVersions(
    rule.managedBy === "builder" ? rule.id : undefined
  );
  const activeVersion =
    versionsQuery.data?.versions.find(
      (version) => version.status === "active"
    ) ?? null;
  const versionDetailQuery = useAgenticLogicRuleVersionDetail(
    rule.id,
    activeVersion?.version ?? null
  );
  const publishedGraph = versionDetailQuery.data?.detail.graph ?? null;

  const versionsSettled =
    !versionsQuery.isLoading &&
    (activeVersion === null || !versionDetailQuery.isLoading);
  const isStale =
    hasUnpublishedRuleSettings(
      rule.settings,
      activeVersion?.settings ?? null
    ) ||
    hasUnpublishedRuleConfig(
      rule.templateParams,
      activeVersion?.templateParams ?? null
    );
  const currentDerived = derived?.key === deriveKey ? derived.graph : null;
  const graph = isStale ? currentDerived : (publishedGraph ?? currentDerived);
  // Template scenarios are described from params server-side; always ready.

  const shouldDerive =
    versionsSettled &&
    (publishedGraph === null || isStale) &&
    Boolean(rule.templateId) &&
    attemptedKey !== deriveKey;

  useEffect(() => {
    if (!shouldDerive || !rule.templateId) {
      return;
    }
    setAttemptedKey(deriveKey);
    void validateMutation
      .mutateAsync({
        body: {
          templateParams: rule.templateParams ?? {},
          settings: rule.settings,
        },
        pathParams: { templateId: rule.templateId },
        queryParams: undefined,
      })
      .then((result) => {
        if (currentDeriveKey.current === deriveKey) {
          setDerived({
            key: deriveKey,
            graph: result.valid ? result.graph : null,
          });
        }
      })
      .catch(() => {
        if (currentDeriveKey.current === deriveKey) {
          setDerived({ key: deriveKey, graph: null });
        }
      });
  }, [shouldDerive, deriveKey]);

  return {
    graph,
    isLoading: !versionsSettled || shouldDerive || validateMutation.isPending,
  };
};
